import React from 'react';
import {withI18n, WithI18nProps} from '@shopify/react-i18n';
import {
  DisplayText,
  Stack,
  TextContainer,
  Icon,
  TextStyle,
  Tooltip,
  Link,
} from '@shopify/polaris';
import {LocationMajorMonotone, ChatMajorMonotone} from '@shopify/polaris-icons';
import {
  Paragraph,
  Heading,
  ProfileImage,
} from 'sections/services-marketplace/experts/components';
import {CurrentOrganization, Address} from 'sections/types';
import {StringMap} from 'utilities/types';
import {Routes} from 'sections/services-marketplace/experts/utilities';
import i18nArgs from '../../locales';

import styles from './ExpertDetails.scss';

const MAX_DISPLAY_LANGUAGE_COUNT = 2;

interface OwnProps {
  profileImage: string;
  expertHandle: string;
  bio?: string;
  locations?: string[];
  languages?: string[];
  languageList: StringMap<string>;
  currentOrganization: Pick<CurrentOrganization, 'businessName' | 'address'>;
}

type CombinedProps = OwnProps & WithI18nProps;

function ExpertDetails({
  profileImage,
  currentOrganization,
  bio,
  languages,
  languageList,
  expertHandle,
  i18n,
}: CombinedProps) {
  const {businessName, address} = currentOrganization;

  return (
    <div className={styles.ExpertDetails}>
      <div className={styles.FlexContainer}>
        <Link url={Routes.profilePath(expertHandle)}>
          <ProfileImage source={profileImage} size="Large" />
        </Link>
        <div className={styles.Info}>
          <TextContainer spacing="tight">
            <DisplayText element="h1" size="extraLarge">
              <div className={styles.BusinessName}>
                <Link url={Routes.profilePath(expertHandle)}>
                  {businessName}
                </Link>
              </div>
            </DisplayText>
            <div className={styles.InfoFlexContainer}>
              <Locations address={address} />
              <Languages languages={languages} languageList={languageList} />
            </div>
          </TextContainer>
        </div>
      </div>
      <Bio
        bio={bio}
        heading={i18n.translate('Profile.ExpertDetails.bioHeading', {
          serviceProvider: businessName,
        })}
      />
    </div>
  );
}

export function Locations({address}: {address: Address}) {
  return (
    <div className={styles.Location}>
      <Stack spacing="extraTight" alignment="center">
        <Icon source={LocationMajorMonotone} color="inkLightest" />
        <Paragraph>
          <TextStyle variation="subdued">
            {address.city}, {address.country}
          </TextStyle>
        </Paragraph>
      </Stack>
    </div>
  );
}

export function Languages({
  languages,
  languageList,
}: {
  languages?: string[];
  languageList: StringMap<string>;
}) {
  if (!languages || !languages.length) return null;

  const formattedLanguages = languages.map(language => languageList[language]);

  const initialLanguagesMarkup = (
    <Paragraph>
      <TextStyle variation="subdued">
        {formattedLanguages.slice(0, MAX_DISPLAY_LANGUAGE_COUNT).join(', ')}
      </TextStyle>
    </Paragraph>
  );

  return (
    <Stack.Item>
      <div className={styles.Languages}>
        <Stack spacing="extraTight" alignment="center">
          <Icon source={ChatMajorMonotone} color="inkLightest" />
          {languages.length <= MAX_DISPLAY_LANGUAGE_COUNT ? (
            initialLanguagesMarkup
          ) : (
            <WrappedWithTooltip
              languages={formattedLanguages}
              tooltipTrigger={initialLanguagesMarkup}
            />
          )}
        </Stack>
      </div>
    </Stack.Item>
  );
}

export function WithTooltip({
  languages,
  tooltipTrigger,
  i18n,
}: {languages: string[]; tooltipTrigger: JSX.Element} & WithI18nProps) {
  const tooltipContent = languages.slice(
    MAX_DISPLAY_LANGUAGE_COUNT,
    languages.length
  );
  return (
    <Tooltip active={false} content={tooltipContent.join(', ')}>
      <Stack spacing="extraTight" alignment="center">
        <div className={styles.TooltipTrigger}>
          {tooltipTrigger}
          <div className={styles.TooltipTriggerCount}>
            <Paragraph>
              <TextStyle variation="subdued">
                (
                {i18n.translate('Profile.ExpertDetails.additionalLanguages', {
                  languageCount: tooltipContent.length,
                })}
                )
              </TextStyle>
            </Paragraph>
          </div>
        </div>
      </Stack>
    </Tooltip>
  );
}

const WrappedWithTooltip = withI18n(i18nArgs)(WithTooltip);

export function Bio({bio, heading}: {bio?: string; heading: string}) {
  return bio ? (
    <div className={styles.Bio}>
      <TextContainer spacing="tight">
        <Heading>{heading}</Heading>
        <Paragraph>{bio}</Paragraph>
      </TextContainer>
    </div>
  ) : null;
}

export default withI18n(i18nArgs)(ExpertDetails);
